import React, {useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {CSSTransition} from 'react-transition-group';
import topup from '../../../common/topup-modal/topup-modal.module.css';
import classes from './addBalance.module.css';
import {deletePaymentAPI, getPaymentLink} from '../api';
import History from './history/history';
import {useTelegram} from '../../../../hooks/useTelegram';
import {getTopupPresetsForTariff} from '../../../../utils/topupPresets';

const AddBalance = ({isOpen, setIsOpen, client, getClient}) => {
    const {webApp} = useTelegram();
    const {presets, defaultAmount} = useMemo(
        () => getTopupPresetsForTariff(client?.tariff_name),
        [client?.tariff_name],
    );
    const [amount, setAmount] = useState(defaultAmount);
    const [payMode, setPayMode] = useState('oneoff');
    const [historyOpen, setHistoryOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setAmount(defaultAmount);
        setPayMode('oneoff');
        setLoading(false);
        setErrorText('');
    }, [isOpen, defaultAmount]);

    const monthlyRub = client?.subscription_monthly_rub;
    const hasSubscription = Boolean(client?.subscription && String(client.subscription).trim());

    const canPay =
        payMode === 'subscription'
            ? monthlyRub != null && monthlyRub >= 1
            : Number.isFinite(Number(amount)) && Number(amount) >= 100;

    async function startPayment() {
        setErrorText('');
        if (payMode === 'subscription') {
            if (monthlyRub == null || monthlyRub < 1) {
                setErrorText('Подписка недоступна для вашего тарифа');
                return;
            }
            setLoading(true);
            try {
                const res = await getPaymentLink(monthlyRub, true);
                if (res) {
                    webApp.openLink(res);
                    setIsOpen(false);
                    setAmount(defaultAmount);
                    setPayMode('oneoff');
                }
            } catch (err) {
                setErrorText(err?.message || 'Не удалось создать ссылку на подписку');
            } finally {
                setLoading(false);
            }
            return;
        }
        const val = Number(amount);
        if (!Number.isFinite(val) || val < 100) {
            setErrorText('Минимальная сумма пополнения - 100р');
            return;
        }
        setLoading(true);
        try {
            const res = await getPaymentLink(val, false);
            if (res) {
                webApp.openLink(res);
                setIsOpen(false);
                setAmount(defaultAmount);
            }
        } catch (err) {
            setErrorText(err?.message || 'Не удалось создать ссылку на оплату');
        } finally {
            setLoading(false);
        }
    }

    async function onCancelSubscription() {
        setLoading(true);
        setErrorText('');
        try {
            await deletePaymentAPI();
            setIsOpen(false);
            setAmount(defaultAmount);
            getClient();
        } catch (err) {
            setErrorText(err?.message || 'Не удалось отменить подписку');
        } finally {
            setLoading(false);
        }
    }

    const portalChildren = (
        <>
            <CSSTransition in={isOpen} timeout={300} classNames="openBlock" unmountOnExit mountOnEnter>
                <div
                    className={`${topup.modalOverlay} ${classes.miniappOverlay}`}
                    onClick={() => setIsOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className={topup.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={topup.modalHeader}>
                            <div className={topup.modalTitle}>Пополнение баланса</div>
                            <button type="button" className={topup.closeBtn} onClick={() => setIsOpen(false)}>
                                Закрыть
                            </button>
                        </div>
                        <div className={topup.topupBody}>
                            {hasSubscription ? (
                                <>
                                    <div className={topup.topupHint}>
                                        У вас активирована ежемесячная подписка. Списание — по тарифу
                                        {monthlyRub != null ? ` (${monthlyRub}₽ при текущем тарифе)` : ''}.
                                    </div>
                                    <button
                                        type="button"
                                        className={topup.topupSubmitBtn}
                                        disabled={loading}
                                        onClick={onCancelSubscription}
                                    >
                                        {loading ? 'Отмена...' : 'Отменить подписку'}
                                    </button>
                                    <button
                                        type="button"
                                        className={classes.historyTriggerBtn}
                                        onClick={() => setHistoryOpen(true)}
                                    >
                                        История пополнений
                                    </button>
                                    {errorText ? <div className={topup.errorText}>{errorText}</div> : null}
                                </>
                            ) : (
                                <>
                                    <div className={topup.topupHint}>
                                        Введите сумму в рублях или выберите один из предложенных вариантов ниже.
                                    </div>
                                    <input
                                        className={topup.topupInput}
                                        type="number"
                                        min={100}
                                        value={amount}
                                        onChange={(e) => {
                                            setPayMode('oneoff');
                                            setAmount(e.target.value === '' ? '' : Number(e.target.value));
                                        }}
                                    />
                                    <div className={topup.topupOptions}>
                                        {monthlyRub != null && monthlyRub >= 1 && (
                                            <button
                                                type="button"
                                                className={`${topup.topupOptionBtn} ${payMode === 'subscription' ? topup.topupOptionActive : ''}`}
                                                onClick={() => {
                                                    setPayMode('subscription');
                                                    setAmount(monthlyRub);
                                                }}
                                                disabled={loading}
                                            >
                                                <span className={topup.optionMain}>Подписка {monthlyRub}₽</span>
                                                <span className={topup.optionSub}>ежемесячно (по тарифу)</span>
                                            </button>
                                        )}
                                        {presets.map((item) => (
                                            <button
                                                key={item.value}
                                                type="button"
                                                className={`${topup.topupOptionBtn} ${payMode === 'oneoff' && Number(amount) === item.value ? topup.topupOptionActive : ''}`}
                                                onClick={() => {
                                                    setPayMode('oneoff');
                                                    setAmount(item.value);
                                                }}
                                                disabled={loading}
                                            >
                                                <span className={topup.optionMain}>
                                                    {item.value}р
                                                    {item.bonus ? <span className={topup.topupBonus}> + {item.bonus}р</span> : null}
                                                </span>
                                                <span className={topup.optionSub}>{item.months}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className={topup.topupHelp}>
                                        Если у вас появились вопросы по пополнению баланса, обратитесь к администратору{' '}
                                        <a
                                            href="https://t.me/easyfastvpn"
                                            className={topup.topupLink}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                webApp.openLink('https://t.me/easyfastvpn');
                                            }}
                                        >
                                            @EasyFastVPN
                                        </a>
                                    </div>
                                    <button
                                        type="button"
                                        className={classes.historyTriggerBtn}
                                        onClick={() => setHistoryOpen(true)}
                                    >
                                        История пополнений
                                    </button>
                                    {errorText ? <div className={topup.errorText}>{errorText}</div> : null}
                                    <button type="button" className={topup.topupSubmitBtn} disabled={loading || !canPay} onClick={startPayment}>
                                        {loading ? 'Создаем ссылку...' : 'Пополнить'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </CSSTransition>
            <History historyOpen={historyOpen} setHistoryOpen={setHistoryOpen} />
        </>
    );

    if (typeof document === 'undefined') {
        return null;
    }
    return createPortal(portalChildren, document.body);
};

export default AddBalance;
